import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { FaCheck, FaDownload, FaPause, FaPlay, FaPlus, FaSyncAlt, FaTrash } from "react-icons/fa";
import { exportShortAnswers, generateShortAnswer, splitShortAnswerQuestions } from "../../api/shortAnswers";
import { answerStateStyles, answerStateAttributes } from "../math/QuestionAnswerState";
import MathText from "../math/MathText";
import ExpandedEditorModal from "./ExpandedEditorModal";

const isComplete = (row) => ["done", "review"].includes(row.status) && Boolean(row.answer.trim());
const labels = { pending: "待產生", running: "產生中", done: "已完成", review: "需人工確認", error: "產生失敗" };

export default function ShortAnswerWorkspace({ authToken, active = true }) {
  const [text, setText] = useState("");
  const [splitSource, setSplitSource] = useState("");
  const [context, setContext] = useState("");
  const [rows, setRows] = useState([]);
  const [model, setModel] = useState("");
  const [title, setTitle] = useState("簡答");
  const [filename, setFilename] = useState("簡答.docx");
  const [schoolLevel, setSchoolLevel] = useState("");
  const [splitting, setSplitting] = useState(false);
  const [running, setRunning] = useState(false);
  const [pausing, setPausing] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [notice, setNotice] = useState("");
  const [failure, setFailure] = useState(false);
  const [vimTarget, setVimTarget] = useState(null);
  useEffect(() => {
    if (!notice) return undefined;
    const timer = window.setTimeout(() => setNotice((current) => current === notice ? "" : current), failure ? 8000 : 4000);
    return () => window.clearTimeout(timer);
  }, [notice, failure]);
  const busyRef = useRef(false);
  const stopRef = useRef(false);
  const controllerRef = useRef(null);
  const nextId = useRef(0);

  useEffect(() => () => {
    stopRef.current = true;
    controllerRef.current?.abort();
  }, []);

  useEffect(() => {
    if (!active && busyRef.current) {
      stopRef.current = true;
      setPausing(true);
    }
  }, [active]);

  // A session change must never continue paid requests under the previous identity.
  useEffect(() => {
    setRows([]);
    setConfirmed(false);
    return () => {
      stopRef.current = true;
      controllerRef.current?.abort();
    };
  }, [authToken]);

  const completeRows = rows.filter(isComplete);
  const sourceChanged = rows.length > 0 && text !== splitSource;
  const busy = splitting || running || exporting;
  const allComplete = rows.length > 0 && rows.every(isComplete);
  const hasInvalidQuestion = rows.some((row) => !row.number.trim() || !row.question.trim());
  const canGenerate = rows.length > 0 && !busy && !sourceChanged && !hasInvalidQuestion;
  const canExport = allComplete && !hasInvalidQuestion && !busy && !sourceChanged && confirmed && title.trim() && filename.trim();

  const tell = (message, error = false) => { setNotice(message); setFailure(error); };
  const patchRow = (id, update) => setRows((current) => current.map((row) => row.id === id ? { ...row, ...update } : row));

  const split = async () => {
    if (busyRef.current || !text.trim()) return;
    busyRef.current = true;
    setSplitting(true);
    setConfirmed(false);
    tell("");
    const controller = new AbortController();
    controllerRef.current = controller;
    try {
      const result = await splitShortAnswerQuestions({ text, authToken, signal: controller.signal });
      if (controller.signal.aborted) return;
      if (!result.success) { tell(result.error, true); return; }
      setRows(result.questions.map((row) => ({ ...row, id: String(++nextId.current), answer: "", status: "pending", error: "" })));
      setSplitSource(text);
      setModel(result.model);
      tell(`已切成 ${result.questions.length} 題。請檢查題號、題目與共用條件，再開始產生簡答。`);
    } finally {
      busyRef.current = false;
      setSplitting(false);
    }
  };

  const generate = async (onlyId) => {
    if (busyRef.current || !canGenerate) return;
    const queue = rows.filter((row) => onlyId ? row.id === onlyId : !isComplete(row));
    if (!queue.length) return;
    busyRef.current = true;
    stopRef.current = false;
    setRunning(true);
    setPausing(false);
    setConfirmed(false);
    tell("");
    const controller = new AbortController();
    controllerRef.current = controller;
    let failed = false;
    try {
      for (const row of queue) {
        if (stopRef.current || controller.signal.aborted) break;
        patchRow(row.id, { status: "running", error: "" });
        const result = await generateShortAnswer({ question: row.question, context, authToken, signal: controller.signal });
        if (controller.signal.aborted) return;
        if (result.success) {
          patchRow(row.id, { answer: result.answer, status: result.needs_review ? "review" : "done", error: "" });
          setModel(result.model);
        } else {
          failed = true;
          patchRow(row.id, { status: "error", error: result.error });
          tell(result.error, true);
          if ([0, 401, 403, 429].includes(result.status) || ["openai_auth_failed", "openai_insufficient_quota", "openai_processing_disabled"].includes(result.code)) {
            stopRef.current = true;
            break;
          }
        }
      }
      if (!failed) tell(stopRef.current ? "已暫停，已完成的簡答會保留。可接續剩餘題目。" : "本次簡答已產生，請檢查預覽；可直接修改答案後匯出。");
    } finally {
      busyRef.current = false;
      setRunning(false);
      setPausing(false);
    }
  };

  const editRow = (id, field, value) => {
    setConfirmed(false);
    if (field === "question") patchRow(id, { question: value, answer: "", status: "pending", error: "" });
    else if (field === "answer") patchRow(id, { answer: value, status: value.trim() ? (value.includes("[需人工確認]") ? "review" : "done") : "pending", error: "" });
    else patchRow(id, { [field]: value });
  };

  const updateSourceText = (value) => { setText(value); setConfirmed(false); };
  const updateContext = (value) => {
    setContext(value);
    setConfirmed(false);
    setRows((current) => current.map((row) => ({ ...row, answer: "", status: "pending", error: "" })));
  };

  const vimRow = rows.find((row) => row.id === vimTarget?.id);
  const vimEditor = vimTarget?.kind === "source" ? {
    key: "short-answer-source", title: "題目文字", value: text,
    onChange: (value) => updateSourceText(value.slice(0, 100000)), defaultVimMode: true,
  } : vimTarget?.kind === "context" ? {
    key: "short-answer-context", title: "共用條件", value: context,
    onChange: (value) => updateContext(value.slice(0, 4000)), defaultVimMode: true,
  } : vimRow && ["question", "answer"].includes(vimTarget?.kind) ? {
    key: `short-answer-${vimTarget.kind}-${vimRow.id}`,
    title: `第 ${rows.findIndex((row) => row.id === vimRow.id) + 1} 題${vimTarget.kind === "question" ? "題目" : "簡答"}`,
    value: vimRow[vimTarget.kind],
    onChange: (value) => editRow(vimRow.id, vimTarget.kind, value.slice(0, vimTarget.kind === "question" ? 8000 : 2000)),
    defaultVimMode: true,
  } : null;

  const download = async () => {
    if (busyRef.current || !canExport) return;
    busyRef.current = true;
    setExporting(true);
    const controller = new AbortController();
    controllerRef.current = controller;
    const outputName = filename.trim().toLowerCase().endsWith(".docx") ? filename.trim() : `${filename.trim()}.docx`;
    try {
      const result = await exportShortAnswers({ title: title.trim(), filename: outputName, rows: rows.map(({ number, answer }) => ({ number, answer })), confirmed, schoolLevel, authToken, signal: controller.signal });
      if (controller.signal.aborted) return;
      if (!result.success) { tell(result.error, true); return; }
      const url = URL.createObjectURL(result.blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = outputName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      tell(`已匯出 ${rows.length} 題簡答。`);
    } finally {
      busyRef.current = false;
      setExporting(false);
    }
  };

  return (
    <Workspace>
      <Intro>貼上題目，逐題產生簡答。完成一題就加入右側預覽，確認內容後匯出 Word。</Intro>
      {notice && <Message role={failure ? "alert" : "status"} $error={failure}>{notice}</Message>}
      <Columns>
        <section>
          <Panel>
            <h2>1. 貼上題目</h2>
            <Field as="div"><span>題目文字</span>
              <TextareaWithVim>
                <TextArea aria-label="題目文字" rows={8} maxLength={100000} value={text} disabled={busy} onChange={(event) => updateSourceText(event.target.value)} placeholder={"1. 計算 2 + 3。\n2. 解方程式 x + 4 = 9。"} />
                <VimButton type="button" aria-label="Vim 編輯題目文字" disabled={busy} onClick={() => setVimTarget({ kind: "source" })}>Vim 編輯</VimButton>
              </TextareaWithVim>
            </Field>
            <Hint>支援 1.、1、或 ① 題號。沒有題號時，以獨立一行 === 分隔。每批最多 100 題。</Hint>
            <Button type="button" disabled={busy || !authToken || !text.trim()} onClick={split}><FaSyncAlt />{splitting ? "切題中…" : rows.length ? "重新切題" : "切題並檢查"}</Button>
            {rows.length > 0 && <Hint>重新切題會清除目前答案；切換上方功能頁籤會保留本頁內容。</Hint>}
            {sourceChanged && <Message role="status">原始文字已修改，請重新切題後再產生或匯出。</Message>}
            <Field as="div"><span>共用條件（選填）</span>
              <TextareaWithVim>
                <TextArea aria-label="共用條件（選填）" rows={2} maxLength={4000} value={context} disabled={busy} onChange={(event) => updateContext(event.target.value)} placeholder="題組共用敘述或整份試卷適用的條件" />
                <VimButton type="button" aria-label="Vim 編輯共用條件" disabled={busy} onClick={() => setVimTarget({ kind: "context" })}>Vim 編輯</VimButton>
              </TextareaWithVim>
            </Field>
            <Hint>共用條件會附在每一題；修改後需重新產生答案。個別題組的敘述請放進對應題目。</Hint>
          </Panel>
          {rows.length > 0 && <Panel>
            <Bar><h2>2. 逐題製作</h2><span>{completeRows.length} / {rows.length} 題完成</span></Bar>
            <progress aria-label="簡答產生進度" value={completeRows.length} max={rows.length} />
            <Actions>
              <Button type="button" $primary disabled={!canGenerate || allComplete} onClick={() => generate()}><FaPlay />{completeRows.length ? "接續未完成題目" : "開始產生簡答"}</Button>
              {running && <Button type="button" disabled={pausing} onClick={() => { stopRef.current = true; setPausing(true); }}><FaPause />{pausing ? "本題完成後暫停…" : "暫停"}</Button>}
            </Actions>
            <Hint>{model ? `${model} · ` : ""}每題只產生簡答，省略詳解與二次校驗。產生中可先查看已完成的預覽。</Hint>
            <QuestionList>
              {rows.map((row, index) => <QuestionCard key={row.id}>
                <Bar>
                  <NumberField>題號<input aria-label={`第 ${index + 1} 題題號`} maxLength={40} value={row.number} disabled={busy} onChange={(event) => editRow(row.id, "number", event.target.value)} /></NumberField>
                  <Badge $status={row.status}>{labels[row.status]}</Badge>
                  <IconButton type="button" aria-label={`移除第 ${index + 1} 題`} disabled={busy} onClick={() => { setRows((current) => current.filter((item) => item.id !== row.id)); setConfirmed(false); }}><FaTrash /></IconButton>
                </Bar>
                <details>
                  <summary>檢查／修改題目</summary>
                  <TextareaWithVim>
                    <TextArea aria-label={`第 ${index + 1} 題題目`} rows={4} maxLength={8000} value={row.question} disabled={busy} onChange={(event) => editRow(row.id, "question", event.target.value)} />
                    <VimButton type="button" aria-label={`Vim 編輯第 ${index + 1} 題題目`} disabled={busy} onClick={() => setVimTarget({ kind: "question", id: row.id })}>Vim 編輯</VimButton>
                  </TextareaWithVim>
                </details>
                <QuestionText><MathText source={row.question} fallback="請填入題目" /></QuestionText>
                <AnswerField as="div" $empty={!row.answer.trim()}><span>簡答</span>
                  <TextareaWithVim>
                    <TextArea aria-label={`第 ${index + 1} 題簡答`} rows={2} maxLength={2000} value={row.answer} disabled={busy} onChange={(event) => editRow(row.id, "answer", event.target.value)} placeholder={row.status === "running" ? "正在產生本題簡答…" : "產生後可修改，也可直接填入答案"} />
                    <VimButton type="button" aria-label={`Vim 編輯第 ${index + 1} 題簡答`} disabled={busy} onClick={() => setVimTarget({ kind: "answer", id: row.id })}>Vim 編輯</VimButton>
                  </TextareaWithVim>
                </AnswerField>
                {row.error && <Message role="alert" $error>{row.error}</Message>}
                <Button type="button" disabled={!canGenerate} onClick={() => generate(row.id)}><FaSyncAlt />{row.answer ? "重新產生本題" : row.status === "error" ? "重試本題" : "產生本題"}</Button>
              </QuestionCard>)}
            </QuestionList>
            <Button type="button" disabled={busy || rows.length >= 100} onClick={() => { setRows((current) => [...current, { id: String(++nextId.current), number: String(current.length + 1), question: "", answer: "", status: "pending", error: "" }]); setConfirmed(false); }}><FaPlus />新增一題</Button>
          </Panel>}
        </section>
        <PreviewColumn>
          <Panel>
            <Bar><h2>3. 簡答預覽</h2><span>{completeRows.length} 題</span></Bar>
            <Field>文件標題<input maxLength={120} value={title} disabled={busy} onChange={(event) => { setTitle(event.target.value); setConfirmed(false); }} /></Field>
            <Paper aria-label="簡答預覽">
              <h3>{title || "簡答"}</h3>
              <table>
                <thead><tr><th>題號</th><th>簡答</th></tr></thead>
                <tbody>{completeRows.map((row) => <tr key={row.id}><td>{row.number}</td><td><MathText source={row.answer} fallback="" /></td></tr>)}</tbody>
              </table>
              {!completeRows.length && <Empty>完成的簡答會逐題顯示在這裡。</Empty>}
            </Paper>
            <Hint>Word 只匯出題號與簡答。若顯示「需人工確認」，請核對題目與答案。</Hint>
            <Field>數字字體適用學段
              <select value={schoolLevel} disabled={busy} onChange={(event) => setSchoolLevel(event.target.value)}>
                <option value="">依文件標題判斷</option>
                <option value="elementary">國小 · Cambria Math</option>
                <option value="secondary">國中／高中 · Times New Roman</option>
              </select>
            </Field>
            <Field>Word 檔名<input maxLength={180} value={filename} disabled={busy} onChange={(event) => { setFilename(event.target.value); setConfirmed(false); }} /></Field>
            <Confirm><input type="checkbox" checked={confirmed} disabled={!allComplete || busy || sourceChanged || hasInvalidQuestion} onChange={(event) => setConfirmed(event.target.checked)} />我已確認全部簡答內容</Confirm>
            <Button type="button" $primary disabled={!canExport} onClick={download}>{exporting ? <FaCheck /> : <FaDownload />}{exporting ? "匯出中…" : "確認並匯出 Word"}</Button>
          </Panel>
        </PreviewColumn>
      </Columns>
      <ExpandedEditorModal
        editor={vimEditor}
        onClose={() => setVimTarget(null)}
        onCopy={(value) => navigator.clipboard?.writeText?.(value)}
      />
    </Workspace>
  );
}

const Workspace = styled.div`
  color: var(--app-text, #202124);
  padding-top: 16px;
  input, textarea, button { font: inherit; }
  input:not([type="checkbox"]), textarea {
    width: 100%; min-width: 0; box-sizing: border-box; border: 1px solid var(--app-border, #d5d7db);
    border-radius: 6px; padding: 9px 10px; color: var(--app-input-text, #202124); background: var(--app-input-bg, #fff);
  }
  input:focus-visible, textarea:focus-visible, button:focus-visible, summary:focus-visible { outline: 2px solid #668bbf; outline-offset: 2px; }
  progress { width: 100%; height: 7px; accent-color: #668bbf; margin: 4px 0 14px; }
`;
const Intro = styled.p`margin: 0 0 18px; color: var(--app-muted-text, #5f6368);`;
const Columns = styled.div`
  display: grid; grid-template-columns: minmax(0, 1.05fr) minmax(0, 1fr); gap: 20px; align-items: start;
  @media (max-width: 1000px) { grid-template-columns: minmax(0, 1fr); }
`;
const Panel = styled.div`
  border: 1px solid var(--app-border, #d5d7db); border-radius: 10px; padding: 20px; margin-bottom: 18px;
  background: var(--app-surface, #fff); min-width: 0;
  h2 { font-size: 18px; font-weight: 800; margin: 0 0 16px; }
  @media (max-width: 560px) { padding: 14px; }
`;
const Field = styled.label`display: grid; gap: 7px; margin: 14px 0 10px; font-size: 14px; font-weight: 600;`;
const AnswerField = styled(Field).attrs(answerStateAttributes)`padding: 10px 12px; border-radius: 6px; ${answerStateStyles}`;
const TextArea = styled.textarea`resize: vertical; line-height: 1.55;`;
const TextareaWithVim = styled.div`
  display: grid;
  gap: 4px;
  min-width: 0;
  > textarea { grid-row: 2; display: block; }
`;
const Hint = styled.p`font-size: 12px; color: var(--app-muted-text, #5f6368); line-height: 1.7; margin: 10px 0;`;
const Bar = styled.div`display: flex; align-items: center; gap: 10px; flex-wrap: wrap; justify-content: space-between; h2 { margin-bottom: 8px; } > span { font-size: 13px; }`;
const Actions = styled.div`display: flex; flex-wrap: wrap; gap: 8px;`;
const Button = styled.button`
  display: inline-flex; align-items: center; justify-content: center; gap: 8px; padding: 9px 13px; min-height: 38px;
  border: 1px solid var(--app-border, #d5d7db); border-radius: 6px; font-weight: 700; cursor: pointer;
  color: ${({ $primary }) => $primary ? "var(--app-nav-active-text, #fff)" : "var(--app-input-text, #202124)"};
  background: ${({ $primary }) => $primary ? "var(--app-nav-active-bg, #202124)" : "var(--app-input-bg, #fff)"};
  &:disabled { opacity: .5; cursor: not-allowed; }
`;
const VimButton = styled(Button)`
  grid-row: 1;
  justify-self: end;
  min-height: 24px;
  padding: 2px 6px;
  line-height: 1.2;
  && { font-size: 11px; }
`;
const IconButton = styled(Button)`padding: 8px; min-height: 32px;`;
const QuestionList = styled.div`display: grid; gap: 14px; margin: 18px 0;`;
const QuestionCard = styled.article`
  border: 1px solid var(--app-border, #d5d7db); border-radius: 8px; padding: 14px;
  summary { cursor: pointer; font-size: 12px; margin: 12px 0; color: var(--app-muted-text, #5f6368); }
`;
const NumberField = styled.label`display: flex; align-items: center; gap: 8px; font-size: 13px; input { max-width: 85px; }`;
const Badge = styled.span`font-size: 12px; font-weight: 700; color: ${({ $status }) => ["error", "review"].includes($status) ? "var(--app-danger-text, #be7149)" : "var(--app-muted-text, #5f6368)"};`;
const QuestionText = styled.div`font-size: 14px; line-height: 1.7; max-height: 160px; overflow: auto;`;
const Message = styled.div`
  padding: 10px 12px; margin: 10px 0 16px; border: 1px solid var(--app-border, #d5d7db); border-radius: 6px;
  border-left: 3px solid ${({ $error }) => $error ? "#c77255" : "#668bbf"}; font-size: 13px; line-height: 1.6;
`;
const PreviewColumn = styled.aside`position: sticky; top: 16px; min-width: 0; @media (max-width: 1000px) { position: static; }`;
const Paper = styled.div`
  background: #fff; color: #202124; border: 1px solid #d5d7db; box-shadow: 0 4px 18px #0000000c;
  padding: 24px; min-height: 220px; max-height: 60vh; overflow: auto; font-family: "BiauKai", "標楷體", "Times New Roman", serif;
  h3 { font-size: 18px; margin: 0 0 16px; overflow-wrap: anywhere; }
  table { border-collapse: collapse; width: 100%; table-layout: fixed; font-size: 16px; }
  th, td { border: 1px solid #adb4bd; padding: 9px 10px; text-align: left; line-height: 1.6; overflow-wrap: anywhere; }
  th:first-child, td:first-child { width: 60px; }
  th { background: #f5f6f8; font-size: 14px; }
  @media (max-width: 560px) { padding: 12px; th:first-child, td:first-child { width: 40px; } }
`;
const Empty = styled.p`text-align: center; font: 14px sans-serif; color: #737b87; padding: 28px 0;`;
const Confirm = styled.label`display: flex; align-items: center; gap: 8px; margin: 16px 0; font-size: 14px; cursor: pointer; input { width: 16px; height: 16px; }`;
